/**
 * Kilograms of CO₂ a young tree absorbs in a year.
 *
 * The same 22 the server uses in `apps/server/src/domain/co2.ts`, which is
 * where the figure is justified. It is repeated here because the profile
 * derives a per-member saving in the browser from that member's own bookings —
 * the API has no per-member impact route. `impact.test.ts` reads the server's
 * copy and fails if the two drift.
 */
export const KG_ABSORBED_PER_TREE_YEAR = 22;

/**
 * Trees-growing-for-a-year equivalent of a saving in grams.
 *
 * Divides once, from grams, exactly as the server's `treesEquivalent` does.
 * Rounding to kilograms first and dividing that disagrees with the API's own
 * answer for the same saving: 32,600 g is 32.6/22 = 1 tree, but rounded to
 * 33 kg first it becomes 33/22 = 1.5, which rounds to 2.
 */
export function treesEquivalent(grams: number): number {
  return Math.round(grams / 1000 / KG_ABSORBED_PER_TREE_YEAR);
}

/** A seat this member holds or held. A request or a cancellation is neither. */
const SEAT_HELD = new Set(['CONFIRMED', 'COMPLETED']);

export function isRealisedBooking(booking: { status: string }): boolean {
  return SEAT_HELD.has(booking.status);
}

/**
 * A ride this member drove that somebody actually joined.
 *
 * `/trips/mine` answers "rides I am driving", which is not the same question:
 * it includes a ride published an hour ago that nobody has booked, and a ride
 * that was cancelled.
 *
 * The test is `seatsConfirmed`, not `seatsTotal - seatsAvailable`. Seats are
 * held by a request the moment it is made, so the difference counts people the
 * driver has not agreed to carry — the same request that the passenger side and
 * the community figure both decline to count until it is confirmed. Reading the
 * held seats made one request count for the driver and for nobody else.
 *
 * A cancelled ride is excluded by name rather than by its seats. Cancellation
 * zeroes `seatsAvailable`, so a held-seats test would have counted exactly the
 * journeys that never happened.
 */
export function isSharedDrive(trip: { status: string; seatsConfirmed: number }): boolean {
  if (trip.status === 'CANCELLED') return false;
  return trip.seatsConfirmed > 0;
}

/**
 * The profile's "trips shared": journeys this member was part of, from either
 * seat. Counted once each — a member cannot be both driver and passenger on
 * the same ride, because booking your own trip is refused.
 */
export function tripsShared(input: {
  driving: readonly { status: string; seatsConfirmed: number }[];
  bookings: readonly { status: string }[];
}): number {
  return (
    input.driving.filter(isSharedDrive).length + input.bookings.filter(isRealisedBooking).length
  );
}
